/**
 * AccountPortfolioModal.jsx
 * DAS-1939: Modal to display portfolio positions filtered by account
 * DAS-1940: Added Rebalance Score column with on-demand Select Now calculation
 * DAS-1969: Added Risk-Adj Q column (Exp Q / residual volatility)
 * DAS-1981: Improved Prev Fund Trend display — shows "No change yet" when trend hasn't changed
 * Shows: Ticker/Company, Exposure, Fund Trend, Consensus, Previous Trend, ESG, Rebalance Score
 */

import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Table, Tag, Tooltip, Spin, message, Button, InputNumber, Alert } from 'antd';
import { 
  ArrowUpOutlined, 
  ArrowDownOutlined, 
  MinusOutlined,
  InfoCircleOutlined,
  CalculatorOutlined,
  LoadingOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { Dialog, DialogTitle, DialogContent } from '@mui/material';
import api from '../functions/api';
import ThreeTablesModal from "./SearchResultModal";
import AlertCard from './AlertCard';
import getUserId from '../functions/getUserId';
import { getCachedAccount, prefetchAccount } from '../functions/accountPortfolioCache';  // DAS-2692: background-preloaded data
import { parseTrendDrivers } from '../helpers/fundamentalTrendDrivers';  // DAS-2722: metric drivers for the trend tooltip
// DAS-2327: shared tooltip content for Return Prob / Exp Q / Reward÷Risk columns
import {
  ExpQReturnTooltip,
  ReturnProbabilityTooltip,
  RewardRiskTooltip,
  ExpQReturnHeaderTooltip,
  ReturnProbabilityHeaderTooltip,
  RewardRiskHeaderTooltip,
} from '../helpers/returnMetricsTooltips';
import { groupAccountColumns, isFundRow } from '../functions/accountColumnGroups';  // DAS-3292
import { sleeveAlpha } from '../functions/alphaCard';  // DAS-3298: the Alpha column
import { registerRowsFrom, daysOld, STALE_AFTER_DAYS } from './AlphaCardBlock';  // DAS-3298
import SaxoSwitchScores from './SaxoSwitchScores';  // DAS-3336

const ALERT_DOT_COLORS = {
  Accumulate: '#52c41a',
  'Step In': '#52c41a',
  Harvest: '#ff4d4f',
  Monitor: '#faad14',
};

const AccountPortfolioModal = ({ visible, onClose, account, accountDisplayName, toplineData, alerts = [] }) => {
  const [loading, setLoading] = useState(false);
  const [portfolioData, setPortfolioData] = useState([]);
  const [selectedTicker, setSelectedTicker] = useState(null);
  const [isTickerModalVisible, setIsTickerModalVisible] = useState(false);
  const userId = getUserId();
  
  // DAS-1940: Rebalance score state
  const [rebalanceScores, setRebalanceScores] = useState(null);
  const [rebalanceLoading, setRebalanceLoading] = useState(false);
  const [rebalanceError, setRebalanceError] = useState(null);
  const [rebalanceInfo, setRebalanceInfo] = useState(null);
  
  // DAS-2157: Alert dot state
  const [alertDialogOpen, setAlertDialogOpen] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState(null);

  // DAS-2306: Local edits + saving state for W&O year_start_price column.
  // Map keyed by ticker so we can render the in-flight value while the PATCH
  // is being persisted, and surface per-row errors without blocking the table.
  const [yspEdits, setYspEdits] = useState({});      // { ticker: numericValue }
  const [yspSaving, setYspSaving] = useState({});    // { ticker: boolean }
  
  const alertsByTicker = useMemo(() => {
    const map = {};
    if (!alerts || !alerts.length) return map;
    alerts.forEach(a => {
      if (!a.ticker) return;
      const key = a.ticker.toUpperCase();
      if (!map[key]) map[key] = [];
      map[key].push(a);
    });
    return map;
  }, [alerts]);

  // DAS-3298: the alpha register (one read per modal open) gives each signal its name and edge
  const [alphaRegister, setAlphaRegister] = useState([]);
  useEffect(() => {
    if (!visible) return;
    let cancelled = false;
    api.get('/alpha_register')
      .then((res) => { if (!cancelled) setAlphaRegister(registerRowsFrom(res && res.data)); })
      .catch(() => { if (!cancelled) setAlphaRegister([]); });
    return () => { cancelled = true; };
  }, [visible]);

  useEffect(() => {
    if (visible && account) {
      // Reset rebalance scores when modal opens for a different account
      setRebalanceScores(null);
      setRebalanceError(null);
      setRebalanceInfo(null);
      // DAS-2692: if this broker was pre-loaded in the background, use the cached
      // data so the modal opens instantly with no "Loading portfolio data..." wait.
      const cached = getCachedAccount(account);
      if (cached) {
        applyPortfolioData(cached.data, cached.debug, cached.universe);
        setLoading(false);
      } else {
        fetchPortfolioByAccount();
      }
    }
  }, [visible, account]);

  const [debugInfo, setDebugInfo] = useState(null);

  // DAS-2692: apply a portfolio_by_account payload to state — used for both a fresh
  // fetch and a background-preloaded cache hit. `universe` = select_now_universe size.
  const applyPortfolioData = (data, debug, universe) => {
    setPortfolioData(data);
    setDebugInfo(debug);
    console.log('DAS-1939 Debug:', debug);

    // DAS-1969: Auto-populate rebalance scores from precomputed data
    const maxAgeSeconds = 168 * 60 * 60; // 168 hours = 1 week
    const nowEpoch = Math.floor(Date.now() / 1000);
    const precomputedScores = {};
    let hasValidScores = false;
    let oldestEpoch = null;

    data.forEach(item => {
          if (item.select_now_position && item.select_now_epoch) {
            const ticker = item.Ticker?.toUpperCase();
            const ageSeconds = nowEpoch - item.select_now_epoch;
            const isFresh = ageSeconds <= maxAgeSeconds;
            const hasSelectionData = item.select_now_selected != null && item.select_now_selected !== '';
            const isMomentumFiltered = item.select_now_momentum_filtered === 1;
            
            if (isFresh) {
              precomputedScores[ticker] = {
                position: item.select_now_position,
                score: item.select_now_score,
                trade: item.select_now_trade || (item.select_now_position <= 10 ? 'BUY' : 'HOLD'),
                selected: hasSelectionData ? (item.select_now_selected === 1) : null,
                selectedTrade: item.select_now_trade || null,
                momentumFiltered: isMomentumFiltered,
                precomputed: true
              };
              hasValidScores = true;
              if (!oldestEpoch || item.select_now_epoch < oldestEpoch) {
                oldestEpoch = item.select_now_epoch;
              }
            } else {
              // DAS-2156 Fix B: Show stale scores in muted style instead of hiding
              const scoreDate = new Date(item.select_now_epoch * 1000).toLocaleDateString();
              precomputedScores[ticker] = {
                position: item.select_now_position,
                score: item.select_now_score,
                trade: item.select_now_trade || 'HOLD',
                selected: hasSelectionData ? (item.select_now_selected === 1) : null,
                selectedTrade: item.select_now_trade || null,
                momentumFiltered: isMomentumFiltered,
                precomputed: true,
                stale: true,
                staleDate: scoreDate
              };
            }
          }
        });
        
    if (hasValidScores || Object.keys(precomputedScores).length > 0) {
      setRebalanceScores(precomputedScores);
      const dataDate = oldestEpoch ? new Date(oldestEpoch * 1000).toLocaleDateString() : null;
      const universeSize = universe || Object.keys(precomputedScores).length;
      setRebalanceInfo({
        precomputed: true,
        asOfDate: dataDate,
        totalRanked: universeSize,
        totalUniverse: universeSize
      });
      console.log('DAS-1969: Using precomputed rebalance scores from', dataDate, 'universe:', universeSize);
    }
  };

  const fetchPortfolioByAccount = async () => {
    setLoading(true);
    try {
      // DAS-2692: prefetchAccount fetches AND populates the shared cache, so a later
      // open of the same broker is instant.
      const entry = await prefetchAccount(userId, account);
      applyPortfolioData(entry.data, entry.debug, entry.universe);
    } catch (error) {
      console.error('Error fetching portfolio by account:', error);
      message.error('Error loading portfolio data');
    } finally {
      setLoading(false);
    }
  };

  // DAS-1940: Fetch rebalance scores via Select Now
  const fetchRebalanceScores = async () => {
    setRebalanceLoading(true);
    setRebalanceError(null);
    
    try {
      // Get tickers from current portfolio
      const tickers = portfolioData.map(p => p.Ticker).filter(Boolean);
      console.log('DAS-1940: Fetching rebalance scores for tickers:', tickers);
      
      const response = await api.post('/backtesting', {
        userId,
        action: 'portfolio_rebalance_scores',
        payload: { tickers }
      });
      
      console.log('DAS-1940: Rebalance scores response:', response.data);
      
      if (response.data.success) {
        const data = response.data.data;
        setRebalanceScores(data.rankings);
        setRebalanceInfo({
          runId: data.run_id,
          configId: data.config_id,
          asOfDate: data.as_of_date,
          totalRanked: data.total_ranked,
          totalUniverse: data.total_universe,
          configName: data.config_name,
          configFilters: data.config_filters,
          savedAt: data.saved_at,
          source: data.source,
          computedAt: data.computed_at,
        });
        message.success(`Rebalance scores calculated (${data.total_ranked} stocks ranked, ${data.total_universe || data.total_ranked} in universe)`);
      } else if (response.data.error === 'no_saved_config') {
        setRebalanceError('no_config');
        message.warning('No saved Select Now configuration found');
      } else {
        console.error('DAS-1940: Backend error:', response.data);
        if (response.data.diagnostics) {
          console.error('DAS-2123: Diagnostics:', JSON.stringify(response.data.diagnostics, null, 2));
        }
        setRebalanceError(response.data.message || 'Failed to calculate scores');
        message.error(response.data.message || 'Failed to calculate rebalance scores');
      }
    } catch (error) {
      console.error('DAS-1940: Error fetching rebalance scores:', error);
      console.error('DAS-1940: Error response:', error.response?.data);
      console.error('DAS-1940: Error status:', error.response?.status);
      console.error('DAS-1940: Error message:', error.message);
      const errorMsg = error.response?.data?.error || error.response?.data?.message || error.message || 'Error calculating rebalance scores';
      setRebalanceError(errorMsg);
      message.error(`Failed to calculate rebalance scores: ${errorMsg}`);
    } finally {
      setRebalanceLoading(false);
    }
  };

  const onRowClick = (record) => {
    setSelectedTicker(record.Ticker);
    setIsTickerModalVisible(true);
  };

  // DAS-2306: PATCH year_start_price for a W&O fund row.
  // The backend derives UserID from the JWT (OWASP A01) and uses parameterized
  // SQL (OWASP A03), so all we send is the ticker + the new price (or null to
  // clear).  On success we patch the local row in-place to keep the YTD
  // calc/reconciliation banner consistent without a full refetch.
  const saveYearStartPrice = async (ticker, value) => {
    if (!ticker) return;
    // Row keys come back from /portfolio_by_account as `Ticker` (uppercase) —
    // see DR_portfolio_by_account.py. Match on Ticker, not ticker.
    const previous = portfolioData.find(r => r.Ticker === ticker)?.year_start_price ?? null;
    const normalized = (value === null || value === undefined || value === '') ? null : Number(value);
    if (normalized !== null && (Number.isNaN(normalized) || !Number.isFinite(normalized))) {
      message.error('Year start price must be a number');
      return;
    }
    if (normalized === previous) return;  // no-op blur

    setYspSaving(prev => ({ ...prev, [ticker]: true }));
    try {
      await api.patch('/portfolio_view/year_start_price', {
        ticker,
        year_start_price: normalized,
      });
      // Update local row so the YTD column re-renders immediately.
      setPortfolioData(prev =>
        prev.map(r => r.Ticker === ticker ? { ...r, year_start_price: normalized } : r)
      );
      setYspEdits(prev => {
        const next = { ...prev };
        delete next[ticker];
        return next;
      });
      message.success(`Year start price updated for ${ticker}`);
    } catch (err) {
      console.error('DAS-2306: PATCH year_start_price failed:', err);
      const msg = err.response?.data?.message || err.message || 'Failed to save year start price';
      message.error(msg);
    } finally {
      setYspSaving(prev => {
        const next = { ...prev };
        delete next[ticker];
        return next;
      });
    }
  };

  // Helper function for trend direction icons
  const getTrendDirectionIcon = (direction) => {
    if (!direction) return <MinusOutlined style={{ color: '#999' }} />;
    const dir = direction.toLowerCase();
    if (dir === 'up' || dir === 'improved') {
      return <ArrowUpOutlined style={{ color: '#52c41a' }} />;
    } else if (dir === 'down' || dir === 'deteriorated') {
      return <ArrowDownOutlined style={{ color: '#ff4d4f' }} />;
    }
    return <MinusOutlined style={{ color: '#999' }} />;
  };

  // Helper function for trend label colors - matches existing Portfolio styling
  const getTrendLabelColor = (label) => {
    if (!label) return 'default';
    const lowerLabel = label.toLowerCase();
    
    // Strong levels - filled/solid colors
    if (lowerLabel.includes('strong') && lowerLabel.includes('improvement')) return '#52c41a'; // solid green
    if (lowerLabel.includes('strong') && lowerLabel.includes('deterioration')) return '#ff4d4f'; // solid red
    
    // Moderate/Mild Improvement - green outline
    if (lowerLabel.includes('improvement')) return 'green';
    
    // Moderate/Mild Deterioration - red outline  
    if (lowerLabel.includes('deterioration')) return 'red';
    
    // Neutral - orange/warning outline
    if (lowerLabel.includes('neutral')) return 'orange';
    
    return 'default';
  };

  // Helper function for ESG rating display - compact colored boxes
  const renderESGRating = (record) => {
    const { sustainalytics_esg, msci_esg, refinitiv_esg } = record;
    const esgItems = [];
    
    // Sustainalytics: lower is better (green <= 20, yellow 20-30, red > 30)
    if (sustainalytics_esg !== null && sustainalytics_esg !== undefined) {
      const saColor = sustainalytics_esg <= 20 ? '#52c41a' : sustainalytics_esg <= 30 ? '#faad14' : '#ff4d4f';
      esgItems.push(
        <Tooltip key="sa" title="Sustainalytics ESG Risk Rating (lower is better)">
          <span style={{ 
            backgroundColor: saColor, 
            color: 'white', 
            padding: '2px 8px', 
            borderRadius: '4px',
            fontSize: '12px',
            fontWeight: 'bold'
          }}>
            {Math.round(sustainalytics_esg)}
          </span>
        </Tooltip>
      );
    }
    
    // MSCI: letter rating
    if (msci_esg) {
      const msciColors = { 'AAA': '#52c41a', 'AA': '#52c41a', 'A': '#73d13d', 'BBB': '#faad14', 'BB': '#fa8c16', 'B': '#fa8c16', 'CCC': '#ff4d4f' };
      esgItems.push(
        <Tooltip key="msci" title="MSCI ESG Rating">
          <span style={{ 
            backgroundColor: msciColors[msci_esg] || '#d9d9d9', 
            color: 'white', 
            padding: '2px 8px', 
            borderRadius: '4px',
            fontSize: '12px',
            fontWeight: 'bold'
          }}>
            {msci_esg}
          </span>
        </Tooltip>
      );
    }
    
    // Refinitiv: higher is better (green >= 70, yellow 50-70, red < 50)
    if (refinitiv_esg !== null && refinitiv_esg !== undefined) {
      const refColor = refinitiv_esg >= 70 ? '#52c41a' : refinitiv_esg >= 50 ? '#faad14' : '#ff4d4f';
      esgItems.push(
        <Tooltip key="ref" title="Refinitiv ESG Score (0-100, higher is better)">
          <span style={{ 
            backgroundColor: refColor, 
            color: 'white', 
            padding: '2px 8px', 
            borderRadius: '4px',
            fontSize: '12px',
            fontWeight: 'bold'
          }}>
            {Math.round(refinitiv_esg)}
          </span>
        </Tooltip>
      );
    }
    
    return esgItems.length > 0 ? <div style={{ display: 'flex', gap: '4px' }}>{esgItems}</div> : <span style={{ color: '#999' }}>N/A</span>;
  };

  // Format date for Previous Trend
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  // DAS-3250: helpers for the relative-performance and risk-adjusted rank mouseovers.
  const fmtPct = (v) => (v === null || v === undefined) ? 'n/a' : `${v >= 0 ? '+' : ''}${Number(v).toFixed(1)}%`;
  const blocksLine = (record) => {
    const n = record.block_count;
    if (!n) return <div style={{ marginTop: 4, color: '#bbb' }}>No purchase blocks recorded (no transactions found for this ticker in this account).</div>;
    return (
      <div style={{ marginTop: 4 }}>
        {n} purchase block{n > 1 ? 's' : ''} ({record.first_block_date}{record.last_block_date && record.last_block_date !== record.first_block_date ? ` → ${record.last_block_date}` : ''}),
        average age {record.average_block_age != null ? Math.round(record.average_block_age) : '?'} days. Blocks are weighted by number of shares.
      </div>
    );
  };
  const relativePerfTip = (record) => (
    <div style={{ fontSize: 12 }}>
      <b>Relative performance: {fmtPct(record.relative_performance)} per year</b>
      {record.relative_performance_rank ? <> — rank #{record.relative_performance_rank} of {record.relative_performance_ranked} in this sleeve</> : null}
      <div style={{ marginTop: 4 }}>
        Return on the shares you still hold, per purchase block, made comparable across holding periods:
        a block held one year or longer is stated as a return per year; a block held under one year
        shows its plain return (not blown up to a year). The absolute Perf above is the same profit
        without the time correction.
      </div>
      {blocksLine(record)}
      <div style={{ marginTop: 4, color: '#aaa' }}>Dividends are not included. Recomputed nightly (03:02).</div>
    </div>
  );
  // DAS-3253: one colour rule for both ranks — quarter of the sleeve the rank falls in.
  const quarterColor = (rank, ranked) => {
    const share = rank / ranked;
    return share <= 0.25 ? '#389e0d' : share <= 0.5 ? '#52c41a' : share <= 0.75 ? '#fa8c16' : '#ff4d4f';
  };
  // DAS-3253: a rank whose OLDEST purchase block is under 90 days rests on too little
  // history to read as evidence — render it grey and say so in the mouseover.
  const YOUNG_RANK_DAYS = 90;
  const rankAgeDays = (record) => {
    if (!record.first_block_date) return null;
    const d = new Date(record.first_block_date);
    if (Number.isNaN(d.getTime())) return null;
    return Math.round((Date.now() - d.getTime()) / 86400000);
  };
  const isYoungRank = (record) => {
    const age = rankAgeDays(record);
    return age !== null && age < YOUNG_RANK_DAYS;
  };
  const youngNote = (record) => isYoungRank(record) ? (
    <div style={{ marginTop: 4, color: '#fa8c16' }}>
      Young: oldest purchase {rankAgeDays(record)} days ago — under {YOUNG_RANK_DAYS} days this rank is luck as much as evidence, so it is shown grey.
    </div>
  ) : null;
  // DAS-3253: the verdict tag. Past = the two sleeve ranks (good when the average
  // share is in the top half); forward = the same bands the Return Prob (>= 55 green)
  // and Exp Q (>= 0 green) columns use. ETF rows carry the pooled default
  // (INSUFFICIENT_DATA, 49.3 / -0.4), which is no forward view at all.
  const VERDICTS = {
    keep:       { label: 'Keep',       color: 'green',   text: 'Has rewarded you so far and the model still expects a positive quarter.' },
    recovering: { label: 'Recovering', color: 'blue',    text: 'Behind since you bought it, but the model expects a positive quarter — a bottom rank is not a reason to sell.' },
    trim:       { label: 'Trim first', color: 'orange',  text: 'Has rewarded you, but the model no longer backs it — the first candidate to trim if you need cash.' },
    weak:       { label: 'Weak',       color: 'red',     text: 'Behind since you bought it and the model does not expect a recovery.' },
  };
  const verdictFor = (record) => {
    const shares = [];
    if (record.relative_performance_rank && record.relative_performance_ranked) shares.push(record.relative_performance_rank / record.relative_performance_ranked);
    if (record.risk_adjusted_rank && record.risk_adjusted_ranked) shares.push(record.risk_adjusted_rank / record.risk_adjusted_ranked);
    if (!shares.length) return null;
    const pastShare = shares.reduce((a, b) => a + b, 0) / shares.length;
    const pastGood = pastShare <= 0.5;
    const prob = record.return_probability;
    const expQ = record.expected_q_return;
    const forwardKnown = prob != null && expQ != null && !isFundRow(record);  // DAS-3292: one fund test for tag and cells
    const forwardGood = forwardKnown ? (prob >= 55 && expQ >= 0) : null;
    let key;
    if (!forwardKnown) key = pastGood ? 'keep' : 'weak';
    else if (pastGood && forwardGood) key = 'keep';
    else if (!pastGood && forwardGood) key = 'recovering';
    else if (pastGood && !forwardGood) key = 'trim';
    else key = 'weak';
    return { key, ...VERDICTS[key], pastShare, forwardKnown, prob, expQ };
  };
  const verdictTip = (record, v) => (
    <div style={{ fontSize: 12 }}>
      <b>{v.label}</b> — {v.text}
      <div style={{ marginTop: 4 }}>
        Past: Perf rank {record.relative_performance_rank ? `#${record.relative_performance_rank}/${record.relative_performance_ranked}` : 'n/a'},
        Risk rank {record.risk_adjusted_rank ? `#${record.risk_adjusted_rank}/${record.risk_adjusted_ranked}` : 'n/a'} ({v.pastShare <= 0.5 ? 'top' : 'bottom'} half of the sleeve).
      </div>
      <div>
        Forward: {v.forwardKnown
          ? <>Return Prob {Number(v.prob).toFixed(1)}%, Exp Quarter Return {fmtPct(v.expQ)} ({v.prob >= 55 && v.expQ >= 0 ? 'positive' : 'not positive'}).</>
          : <>no forward view for this fund — the tag rests on the past only.</>}
      </div>
      {youngNote(record)}
      <div style={{ marginTop: 4, color: '#aaa' }}>Keep = good on both · Recovering = past weak, forward good · Trim first = past good, forward weak · Weak = both weak.</div>
    </div>
  );
  const verdictTag = (record) => {
    const v = verdictFor(record);
    if (!v) return null;
    return (
      <Tooltip overlayStyle={{ maxWidth: 360 }} title={verdictTip(record, v)}>
        <Tag data-testid="verdict-tag" data-verdict={v.key} color={isYoungRank(record) ? 'default' : v.color}
             style={{ margin: '3px 0 0', fontSize: '10px', lineHeight: '16px', height: '18px', cursor: 'help', opacity: isYoungRank(record) ? 0.7 : 1 }}>
          {v.label}{isYoungRank(record) ? ' · young' : ''}
        </Tag>
      </Tooltip>
    );
  };
  // DAS-3253: the "since the trend changed" line lives in the Fundamentals Trend cell
  // (it belongs to the change date shown there), no longer in the Perf cell.
  const sinceTrendLine = (record) => {
    const since = record.perf_since_trend_change;
    if (since === null || since === undefined || !record.fundamental_trend_label_changed_at) return null;
    return (
      <Tooltip title={
        <div style={{ fontSize: 12 }}>
          Price move since the Fundamentals Trend last changed on <b>{formatDate(record.fundamental_trend_label_changed_at)}</b>
          {record.fundamental_trend_label_previous && <> (from <b>{record.fundamental_trend_label_previous}</b> to <b>{record.fundamental_trend_label}</b>)</>}.
          {record.fundamental_trend_label_changed_price && record.Close && (
            <div style={{ marginTop: 4 }}>Price then {Number(record.fundamental_trend_label_changed_price).toFixed(2)} → now {Number(record.Close).toFixed(2)} (native currency).</div>
          )}
        </div>
      }>
        <div data-testid="perf-since-trend-change" style={{ fontSize: '10px', lineHeight: 1.3, marginTop: '2px', cursor: 'help', color: since >= 0 ? '#52c41a' : '#ff4d4f' }}>
          price {since >= 0 ? '+' : ''}{since.toFixed(1)}% since
        </div>
      </Tooltip>
    );
  };
  const riskAdjTip = (record) => (
    <div style={{ fontSize: 12 }}>
      <b>Risk-adjusted score: {Number(record.risk_adjusted_score).toFixed(2)}</b>
      {record.risk_adjusted_rank ? <> — rank #{record.risk_adjusted_rank} of {record.risk_adjusted_ranked} in this sleeve</> : null}
      <div style={{ marginTop: 4 }}>
        Sharpe ratio since purchase: the return per year of the price since you bought, minus the
        risk-free rate on the buy date, divided by how much the price swung per year over that period.
        Above 1 = well paid for the risk taken; below 0 = the holding lost against a risk-free bond.
        Relative performance: {fmtPct(record.relative_performance)} per year.
      </div>
      {blocksLine(record)}
      <div style={{ marginTop: 4, color: '#aaa' }}>Blocks held under ~3 months use the last ~3 months of prices. Recomputed nightly (03:02).</div>
    </div>
  );

  // DAS-2721: per-value explanation of a fundamental-trend LABEL. Explains that the
  // label is a CHANGE (vs ~1 quarter ago), which score band it falls in, and why it
  // moved a level. Uses only fields already in the row; the metric-by-metric drivers
  // (revenue/EPS/margins/…) live in Fundamental_data_trends_per_ticker and are not in
  // this payload — surfacing those is a follow-up (needs the endpoint to return them).
  const TREND_BANDS = {
    'Strong Improvement': 'score ≥ 17',
    'Moderate Improvement': 'score 9–16',
    'Mild Improvement': 'score 3–8',
    'Neutral': 'score −3 to 3',
    'Mild Deterioration': 'score −4 to −9',
    'Moderate Deterioration': 'score −10 to −17',
    'Strong Deterioration': 'score < −17',
  };
  const fundTrendTip = (record) => {
    const label = record.fundamental_trend_label;
    if (!label) return null;
    const score = record.fundamental_trend_score;
    const band = TREND_BANDS[label];
    const prev = record.fundamental_trend_label_previous;
    const mag = record.fundamental_trend_label_change_magnitude;
    const isNeg = (record.fundamental_trend_label_direction || '').toLowerCase().includes('neg');
    const drivers = parseTrendDrivers(record.fundamental_trend_analysis_text);
    return (
      <div style={{ maxWidth: 260, fontSize: 12 }}>
        <div><b>{label}</b> — how much the fundamentals <b>changed vs ~1 quarter ago</b> (13 weeks), sector-adjusted. This is the direction &amp; size of the <b>change</b>, not the absolute level.</div>
        {score !== null && score !== undefined && (
          <div style={{ marginTop: 4 }}>Trend score: <b>{score}</b> of ~29{band ? ` — “${label}” = ${band}` : ''}.</div>
        )}
        {prev && record.fundamental_trend_label_changed_at && (
          <div style={{ marginTop: 4 }}>
            Moved from <b>{prev}</b> on {formatDate(record.fundamental_trend_label_changed_at)}
            {mag !== null && mag !== undefined ? ` — ${isNeg ? 'down' : 'up'} ${Math.abs(mag)} level${Math.abs(mag) === 1 ? '' : 's'} (the score crossed the band threshold)` : ''}.
          </div>
        )}
        {(drivers.up.length > 0 || drivers.down.length > 0) && (
          <div data-testid="fund-trend-drivers" style={{ marginTop: 6, borderTop: '1px solid #444', paddingTop: 4 }}>
            <div style={{ color: '#bbb', fontSize: 10, marginBottom: 2 }}>What moved it (vs ~1 quarter ago):</div>
            {drivers.up.length > 0 && (
              <div style={{ color: '#95de64' }}>▲ {drivers.up.join(', ')}</div>
            )}
            {drivers.down.length > 0 && (
              <div style={{ color: '#ff7875' }}>▼ {drivers.down.join(', ')}</div>
            )}
          </div>
        )}
        <div style={{ marginTop: 6, color: '#aaa', fontSize: 10 }}>For absolute quality (is it actually good?), see Broad Trend / Zacks.</div>
      </div>
    );
  };

  const columns = [
    {
      title: 'Ticker / Company',
      key: 'ticker_company',
      width: 160,
      fixed: 'left', // DAS-3067: keep ticker visible while scrolling sideways
      render: (_, record) => {
        const tickerUpper = record.Ticker?.toUpperCase();
        const tickerAlerts = alertsByTicker[tickerUpper] || [];
        return (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              {tickerAlerts.map((alert, idx) => {
                const dotColor = ALERT_DOT_COLORS[alert.alert_type] || '#999';
                return (
                  <Tooltip
                    key={alert.ID || idx}
                    title={`${alert.alert_type}${alert.alert_subtype ? ` — ${alert.alert_subtype}` : ''}: ${alert.pf_alert_text || 'No details'}`}
                  >
                    <span
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedAlert(alert);
                        setAlertDialogOpen(true);
                      }}
                      style={{
                        display: 'inline-block',
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        backgroundColor: dotColor,
                        cursor: 'pointer',
                        flexShrink: 0,
                      }}
                    />
                  </Tooltip>
                );
              })}
              <a 
                onClick={() => onRowClick(record)} 
                style={{ cursor: 'pointer', fontWeight: 'bold', color: '#1890ff' }}
              >
                {record.Ticker}
              </a>
              {record.Likely_Institutional_Entry === 1 && (
                <Tooltip title={record.institutional_trend_text || 'Likely Institutional Entry'}>
                  <WarningOutlined style={{ color: '#52c41a', fontSize: 12, marginLeft: 2 }} />
                </Tooltip>
              )}
              {record.Likely_Institutional_Exit === 1 && (
                <Tooltip title={record.institutional_trend_text || 'Likely Institutional Exit'}>
                  <WarningOutlined style={{ color: '#ff4d4f', fontSize: 12, marginLeft: 2 }} />
                </Tooltip>
              )}
            </div>
            <div style={{ fontSize: '12px', color: '#666' }}>
              {record.Company}
            </div>
            {verdictTag(record)}
          </div>
        );
      },
    },
    {
      // DAS-3298: the best single proven signal the stock carries today (the stored alpha card,
      // DAS-3256), as EUR per 10,000; never a sum (DAS-3255). Funds carry no card.
      title: (
        <Tooltip title="The register's measured average for stocks carrying this signal, over its horizon, against similar stocks (Account -> Alpha). Not a forecast of this holding. Largest single signal, never a sum.">
          <span>Alpha <InfoCircleOutlined style={{ fontSize: 11, color: '#999' }} /></span>
        </Tooltip>
      ),
      key: 'alpha',
      width: 110,
      sorter: (a, b) => (sleeveAlpha(a, alphaRegister).amount ?? -1) - (sleeveAlpha(b, alphaRegister).amount ?? -1),
      render: (_, record) => {
        if (isFundRow(record)) return <span data-testid="sleeve-alpha-cell" style={{ color: '#ccc' }}>—</span>;
        const cell = sleeveAlpha(record, alphaRegister);
        const age = cell.date ? daysOld(cell.date) : null;
        const tip = (
          <div style={{ fontSize: 11, maxWidth: 360 }}>
            {cell.lines.map((l) => (
              <div key={l.key} style={{ marginBottom: 2 }}>
                <b>{l.carries === true ? '✓' : l.carries === false ? '—' : '?'}</b> {l.label}: {l.valueText}{l.edgeText ? ` — ${l.edgeText}` : ''}
              </div>
            ))}
            {cell.date && <div style={{ color: '#bbb', marginTop: 4 }}>card of {cell.date}{age != null && age > STALE_AFTER_DAYS ? ` (${age} days old)` : ''}</div>}
            {cell.state === 'unknown' && <div>No alpha card stored for this stock yet.</div>}
          </div>
        );
        if (cell.state === 'unknown') {
          return <Tooltip title={tip}><span data-testid="sleeve-alpha-cell" style={{ color: '#d48806' }}>?</span></Tooltip>;
        }
        if (cell.state === 'none') {
          return <Tooltip title={tip}><span data-testid="sleeve-alpha-cell" style={{ color: '#999' }}>none</span></Tooltip>;
        }
        return (
          <Tooltip title={tip}>
            <div data-testid="sleeve-alpha-cell" style={{ lineHeight: 1.2 }}>
              <div style={{ color: '#389e0d', fontWeight: 'bold' }}>€{cell.amount.toLocaleString('en-US')} / 10k</div>
              <div style={{ fontSize: 10, color: '#666' }}>{cell.label}{cell.horizon ? `, ${cell.horizon}` : ''}</div>
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Exposure',
      key: 'exposure',
      width: 120,
      sorter: (a, b) => (a.Value_eur || 0) - (b.Value_eur || 0),
      defaultSortOrder: 'descend',
      render: (_, record) => (
        // DAS-2306: dropped trailing `∨` (U+2228) glyph — was decorative,
        // carried no data, looked like a status flag in the W&O view.
        <div>
          €{record.Value_eur ? record.Value_eur.toLocaleString('nl-NL', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) : '0'} / {record.Position || 0}
        </div>
      ),
    },
    {
      // DAS-3061: Perf = profit on the shares held now / what was paid for them (DeGiro side
      // switched from DeGiro's plBase, which shrank with every partial sale).
      title: (
        <Tooltip title="Profit or loss on the shares you hold now, compared with what you paid for those shares. Partial sales in the past do not change it.">
          <span data-testid="perf-header" style={{ cursor: 'help', borderBottom: '1px dashed #999' }}>Perf</span>
        </Tooltip>
      ),
      key: 'performance',
      width: 100,  // DAS-3250: room for the "#rank/N (+x.x%/yr)" line
      sorter: (a, b) => (a.performance || 0) - (b.performance || 0),
      render: (_, record) => {
        const perf = record.performance;
        // DAS-3013 "since the trend changed" line: moved to the Fundamentals Trend cell (DAS-3253).

        let main;
        if (perf === null || perf === undefined) {
          // DAS-1981: Show informative message for missing performance
          if (record.performance_status === 'no_cost_basis') {
            main = (
              <Tooltip title="DeGiro does not provide cost basis for this position (likely a transferred position). Performance cannot be calculated without a buy price.">
                <span style={{ color: '#bbb', fontSize: '11px', cursor: 'help', borderBottom: '1px dashed #ccc' }}>No basis</span>
              </Tooltip>
            );
          } else {
            main = <span style={{ color: '#999' }}>N/A</span>;
          }
        } else {
          const color = perf >= 0 ? '#52c41a' : '#ff4d4f';
          main = (
            <span style={{ color, fontWeight: 'bold' }}>
              {perf >= 0 ? '+' : ''}{perf.toFixed(1)}%
            </span>
          );
        }
        // DAS-3250: rank of this position within the sleeve on the relative
        // performance (return per year on the blocks held, plain return under one year).
        const relRank = record.relative_performance_rank;
        const relRanked = record.relative_performance_ranked;
        const rankLine = (relRank !== null && relRank !== undefined && relRanked) ? (
          <Tooltip overlayStyle={{ maxWidth: 340 }} title={relativePerfTip(record)}>
            {/* DAS-3253: same quarter colour as the Risk rank; grey when the rank is young */}
            <div data-testid="perf-rank" style={{ fontSize: '11px', marginTop: '2px', cursor: 'help', display: 'inline-block', color: isYoungRank(record) ? '#bbb' : quarterColor(relRank, relRanked), fontWeight: 'bold' }}>
              #{relRank}/{relRanked} <span style={{ color: '#888', fontWeight: 'normal' }}>({fmtPct(record.relative_performance)}/yr)</span>
            </div>
          </Tooltip>
        ) : (
          <Tooltip title="No relative performance: no purchase blocks are recorded for this position (see the mouseover on the Risk column for details).">
            <div data-testid="perf-rank-none" style={{ fontSize: '10px', marginTop: '2px', color: '#bbb', cursor: 'help' }}>no rank</div>
          </Tooltip>
        );
        return (
          <div>
            {main}
            {rankLine}
          </div>
        );
      },
    },
    {
      // DAS-3250: rank within the sleeve on the risk-adjusted performance score
      // (Sharpe ratio of each block over the period it has been held).
      title: (
        <Tooltip overlayStyle={{ maxWidth: 340 }} title={
          <div style={{ fontSize: 12 }}>
            <b>Risk-adjusted performance rank</b> within this sleeve. #1 = best return per unit of
            price swing since you bought. The score is the Sharpe ratio of each purchase block over
            the period you have held it (daily price moves since the buy date, minus the risk-free
            rate of that date), averaged over the blocks by number of shares. A block held under
            ~3 months uses the last ~3 months of prices. Hover a rank for the numbers.
          </div>
        }>
          <span data-testid="risk-header" style={{ cursor: 'help', borderBottom: '1px dashed #999' }}>Risk</span>
        </Tooltip>
      ),
      key: 'risk_rank',
      width: 70,
      sorter: (a, b) => (a.risk_adjusted_rank ?? Number.MAX_SAFE_INTEGER) - (b.risk_adjusted_rank ?? Number.MAX_SAFE_INTEGER),
      render: (_, record) => {
        const rank = record.risk_adjusted_rank;
        const ranked = record.risk_adjusted_ranked;
        if (rank === null || rank === undefined || !ranked) {
          return (
            <Tooltip title={relativePerfTip(record)}>
              <span data-testid="risk-rank-none" style={{ color: '#bbb', fontSize: '11px', cursor: 'help' }}>N/A</span>
            </Tooltip>
          );
        }
        // DAS-3253: shared quarter colour; grey when the rank is young
        const color = isYoungRank(record) ? '#bbb' : quarterColor(rank, ranked);
        return (
          <Tooltip overlayStyle={{ maxWidth: 340 }} title={riskAdjTip(record)}>
            <span data-testid="risk-rank" style={{ cursor: 'help' }}>
              <span style={{ color, fontWeight: 'bold' }}>#{rank}</span>
              <span style={{ color: '#888', fontSize: '11px' }}>/{ranked}</span>
              <div style={{ fontSize: '10px', color: '#888' }}>{Number(record.risk_adjusted_score).toFixed(2)}</div>
            </span>
          </Tooltip>
        );
      },
    },
    {
      // DAS-2251: Merged "Prev Fund Trend" into this column
      title: (
        <span>
          Fundamentals Trend <Tooltip title={
            <div style={{ maxWidth: 260, fontSize: 12 }}>
              Current fundamental trend label; previous trend shown below with the date it changed, direction and magnitude.
              <div style={{ marginTop: 6 }}>
                This is the <b>change vs ~1 quarter ago</b> (13 weeks), <b>not the absolute level</b> — a stock can be “Strong Improvement” while its fundamentals are still average. For absolute quality see <b>Broad Trend</b> / <b>Zacks</b>. Hover a label for details.
              </div>
            </div>
          }><InfoCircleOutlined /></Tooltip>
        </span>
      ),
      key: 'fund_trend',
      width: 140,
      render: (_, record) => (
        <div>
          {record.fundamental_trend_label ? (
            <Tooltip title={fundTrendTip(record)}>
              {/* DAS-2714: hover target for the auditor's `verify: ui hover` marker
                  (DAS-2722 — the drivers list only mounts on hover) */}
              <Tag data-testid="fund-trend-label" color={getTrendLabelColor(record.fundamental_trend_label)} style={{ margin: 0, cursor: 'help' }}>
                {record.fundamental_trend_label}
              </Tag>
            </Tooltip>
          ) : (
            <span style={{ color: '#999' }}>N/A</span>
          )}
          {record.fundamental_trend_label_previous ? (
            <div style={{ fontSize: '10px', color: '#888', marginTop: '3px', lineHeight: 1.3 }}>
              <Tag color={getTrendLabelColor(record.fundamental_trend_label_previous)} style={{ margin: 0, fontSize: '10px', lineHeight: '16px', height: '18px' }}>
                {record.fundamental_trend_label_previous}
              </Tag>
              {record.fundamental_trend_label_changed_at && (
                <div style={{ marginTop: '1px' }}>{formatDate(record.fundamental_trend_label_changed_at)}</div>
              )}
              {record.fundamental_trend_label_direction && (
                <div style={{ color: record.fundamental_trend_label_direction.toLowerCase().includes('negative') ? '#ff4d4f' : '#52c41a' }}>
                  {record.fundamental_trend_label_direction}
                  {record.fundamental_trend_label_change_magnitude !== null && record.fundamental_trend_label_change_magnitude !== undefined && (
                    <span> ({record.fundamental_trend_label_change_magnitude} lvl)</span>
                  )}
                </div>
              )}
            </div>
          ) : record.fundamental_trend_label ? (
            <Tooltip title="Current trend has not changed since tracking started — no previous trend recorded yet">
              <div style={{ fontSize: '10px', color: '#bbb', marginTop: '2px' }}>No change yet</div>
            </Tooltip>
          ) : null}
          {sinceTrendLine(record)}
        </div>
      ),
    },
    {
      // DAS-1968: Improved header and tooltips for Consensus column
      title: (
        <Tooltip 
          title={
            <div style={{ maxWidth: 280 }}>
              <div style={{ fontWeight: 'bold', marginBottom: 8 }}>Trend Score (0-100)</div>
              <div style={{ marginBottom: 8 }}>
                Overall trend health combining:
                <br />• Price momentum (35%)
                <br />• Market sentiment (20%)
                <br />• Fundamental trend (25%)
                <br />• Institutional flow (20%)
              </div>
              <div style={{ fontWeight: 'bold', marginBottom: 4 }}>Fundamentals Trend (~-29 to +29)</div>
              <div>
                • ≥17: Strong Improvement
                <br />• ≥9: Moderate Improvement
                <br />• ≥3: Mild Improvement
                <br />• -3 to 3: Neutral
                <br />• ≤-3: Deteriorating
              </div>
              <div style={{ marginTop: 8, fontSize: '10px', color: '#aaa', borderTop: '1px solid #444', paddingTop: 4 }}>
                Hover over a value to see the analysis date
              </div>
            </div>
          }
        >
          <span style={{ cursor: 'help', borderBottom: '1px dashed #999' }}>
            Broad Trend<br /><span style={{ fontSize: '10px', color: '#888' }}>(Fund Trend ↓)</span>
          </span>
        </Tooltip>
      ),
      key: 'consensus',
      width: 85,
      sorter: (a, b) => (a.consensus_score || 0) - (b.consensus_score || 0),
      render: (_, record) => {
        // Color based on score: green >= 63, orange 55-62, red < 55
        const getScoreColor = (score) => {
          if (score >= 63) return '#52c41a'; // green
          if (score >= 55) return '#fa8c16'; // orange
          return '#ff4d4f'; // red
        };
        
        // DAS-1968: Color for fundamental trend (range: ~-29 to +29)
        // Thresholds aligned with fundamental_trend_label derivation (pct of max 29)
        const getFundTrendColor = (score) => {
          if (score >= 17) return '#389e0d'; // dark green - Strong Improvement (pct >= 60%)
          if (score >= 9) return '#52c41a';  // green - Moderate Improvement (pct >= 30%)
          if (score >= 3) return '#95de64';  // light green - Mild Improvement (pct >= 10%)
          if (score >= -3) return '#888';    // gray - Neutral (pct >= -10%)
          if (score >= -9) return '#fa8c16'; // orange - Mild Deterioration (pct >= -30%)
          if (score >= -17) return '#ff7a45';// dark orange - Moderate Deterioration (pct >= -60%)
          return '#ff4d4f';                  // red - Strong Deterioration (pct < -60%)
        };
        
        // DAS-1968: Check for divergence (high trend but fundamentals below "Moderate Improvement")
        const hasDivergence = record.consensus_score >= 55 && record.fundamental_trend_score < 9;
        
        // DAS-1968: Build detailed tooltip for values
        const valueTooltip = record.consensus_score !== null ? (
          <div style={{ maxWidth: 260 }}>
            <div style={{ fontWeight: 'bold', marginBottom: 6 }}>
              {record.Ticker || record.ticker} Trend Analysis
            </div>
            <div style={{ marginBottom: 6 }}>
              <strong>Overall Trend: {Math.round(record.consensus_score)}/100</strong>
              <br />
              <span style={{ fontSize: '11px', color: '#ccc' }}>
                {record.consensus_score >= 63 ? 'Bullish' : record.consensus_score >= 55 ? 'Moderate' : record.consensus_score >= 45 ? 'Neutral' : 'Bearish'}
              </span>
            </div>
            {record.fundamental_trend_score !== null && record.fundamental_trend_score !== undefined && (
              <div style={{ marginBottom: 6 }}>
                <strong>Fundamentals: {Math.round(record.fundamental_trend_score)}</strong>
                <span style={{ fontSize: '11px', color: '#ccc' }}> (range: ~-29 to +29)</span>
                <br />
                <span style={{ fontSize: '11px', color: '#ccc' }}>
                  {record.fundamental_trend_score >= 17 ? 'Strong Improvement' : 
                   record.fundamental_trend_score >= 9 ? 'Moderate Improvement' :
                   record.fundamental_trend_score >= 3 ? 'Mild Improvement' :
                   record.fundamental_trend_score >= -3 ? 'Neutral' :
                   record.fundamental_trend_score >= -9 ? 'Mild Deterioration' :
                   record.fundamental_trend_score >= -17 ? 'Moderate Deterioration' : 'Strong Deterioration'}
                </span>
              </div>
            )}
            {hasDivergence && (
              <div style={{ color: '#faad14', fontSize: '11px', borderTop: '1px solid #444', paddingTop: 6, marginTop: 6 }}>
                ⚠️ Divergence: Good momentum but weak fundamentals. May be sentiment-driven.
              </div>
            )}
            {(record.consensus_updated_at || record.fundamental_trend_updated_at) && (
              <div style={{ fontSize: '10px', color: '#999', borderTop: '1px solid #444', paddingTop: 4, marginTop: 4 }}>
                {record.consensus_updated_at && <>Trend analysis: {record.consensus_updated_at}</>}
                {record.fundamental_trend_updated_at && <><br/>Fundamentals: {record.fundamental_trend_updated_at}</>}
              </div>
            )}
          </div>
        ) : null;
        
        return (
          <Tooltip title={valueTooltip} placement="left">
            <div style={{ cursor: 'help' }}>
              {record.consensus_score !== null && record.consensus_score !== undefined ? (
                <>
                  <div style={{ fontWeight: 'bold', color: getScoreColor(record.consensus_score) }}>
                    {Math.round(record.consensus_score)}
                  </div>
                  {record.fundamental_trend_score !== null && record.fundamental_trend_score !== undefined && (
                    <div style={{ fontSize: '12px', color: getFundTrendColor(record.fundamental_trend_score) }}>
                      {Math.round(record.fundamental_trend_score)}
                      {hasDivergence && <span style={{ marginLeft: 2 }}>⚠️</span>}
                    </div>
                  )}
                </>
              ) : (
                <span style={{ color: '#999' }}>N/A</span>
              )}
            </div>
          </Tooltip>
        );
      },
    },
    {
      // DAS-1960: Return Probability column
      // DAS-2327: tooltip content moved to shared helper
      title: (
        <Tooltip title={<ReturnProbabilityHeaderTooltip />} overlayStyle={{ maxWidth: 360 }}>
          <span style={{ cursor: 'help', borderBottom: '1px dashed #999' }}>Return<br/>Prob</span>
        </Tooltip>
      ),
      key: 'return_prob',
      width: 110,
      sorter: (a, b) => (a.return_probability || 0) - (b.return_probability || 0),
      render: (_, record) => {
        const prob = record.return_probability;
        if (prob === null || prob === undefined) {
          return <span style={{ color: '#999' }}>N/A</span>;
        }
        // Color: green >= 55, orange 45-55, red < 45
        const color = prob >= 55 ? '#52c41a' : prob >= 45 ? '#fa8c16' : '#ff4d4f';
        // DAS-2327: expQ / resVol / zScore locals removed — the tooltip now
        // pulls them directly from `record` via the shared helper.

        // DAS-2089: Week-ago comparison
        const probWeekAgo = record.return_probability_week_ago;
        const probDelta = (prob != null && probWeekAgo != null) ? prob - probWeekAgo : null;
        const probArrow = probDelta != null && probDelta !== 0
          ? (probDelta > 0 ? <span style={{ color: '#52c41a', marginLeft: 2 }}>▲</span> : <span style={{ color: '#ff4d4f', marginLeft: 2 }}>▼</span>)
          : null;
        
        return (
          <Tooltip
            overlayStyle={{ maxWidth: 360 }}
            title={<ReturnProbabilityTooltip record={record} />}
          >
            <span data-testid="return-prob-value" style={{ cursor: 'help' }}>
              <span style={{ color, fontWeight: 'bold' }}>{prob.toFixed(1)}%</span>
              {probArrow}
              {probWeekAgo != null && <span style={{ color: '#888', fontSize: '10px', marginLeft: 3 }}>({probWeekAgo.toFixed(1)})</span>}
            </span>
          </Tooltip>
        );
      },
    },
    {
      // DAS-1960: Expected Q Return column
      // DAS-1969: Renamed to "Exp Quarter Return"
      // DAS-2327: tooltip content moved to shared helper
      title: (
        <Tooltip title={<ExpQReturnHeaderTooltip />} overlayStyle={{ maxWidth: 360 }}>
          <span style={{ cursor: 'help', borderBottom: '1px dashed #999' }}>Exp Quarter<br/>Return</span>
        </Tooltip>
      ),
      key: 'expected_q',
      width: 115,
      sorter: (a, b) => (a.expected_q_return || 0) - (b.expected_q_return || 0),
      render: (_, record) => {
        const expQ = record.expected_q_return;
        if (expQ === null || expQ === undefined) {
          return <span style={{ color: '#999' }}>N/A</span>;
        }
        const color = expQ >= 0 ? '#52c41a' : '#ff4d4f';
        // DAS-2327: regime / baseReturn / alpha locals removed — the shared
        // tooltip reads them directly from `record`.

        // DAS-2089: Week-ago comparison
        const expQWeekAgo = record.expected_q_return_week_ago;
        const expQDelta = (expQ != null && expQWeekAgo != null) ? expQ - expQWeekAgo : null;
        const expQArrow = expQDelta != null && expQDelta !== 0
          ? (expQDelta > 0 ? <span style={{ color: '#52c41a', marginLeft: 2 }}>▲</span> : <span style={{ color: '#ff4d4f', marginLeft: 2 }}>▼</span>)
          : null;
        
        return (
          <Tooltip
            overlayStyle={{ maxWidth: 360 }}
            title={<ExpQReturnTooltip record={record} />}
          >
            <span style={{ cursor: 'help' }}>
              <span style={{ color, fontWeight: 'bold' }}>{expQ >= 0 ? '+' : ''}{expQ.toFixed(1)}%</span>
              {expQArrow}
              {expQWeekAgo != null && <span style={{ color: '#888', fontSize: '10px', marginLeft: 3 }}>({expQWeekAgo >= 0 ? '+' : ''}{expQWeekAgo.toFixed(1)})</span>}
            </span>
          </Tooltip>
        );
      },
    },
    {
      // DAS-1969: Risk-Adjusted Expected Q Return column
      // DAS-2327: tooltip content moved to shared helper — replaced the
      // "wind/waves" metaphor with explicit "Information Ratio" framing.
      title: (
        <Tooltip title={<RewardRiskHeaderTooltip />} overlayStyle={{ maxWidth: 360 }}>
          <span style={{ cursor: 'help', borderBottom: '1px dashed #999' }}>Reward<br/>÷ Risk</span>
        </Tooltip>
      ),
      key: 'risk_adj_q',
      width: 110,
      sorter: (a, b) => (a.risk_adj_q_return || 0) - (b.risk_adj_q_return || 0),
      render: (_, record) => {
        const riskAdjQ = record.risk_adj_q_return;
        if (riskAdjQ === null || riskAdjQ === undefined) {
          return <span style={{ color: '#999' }}>N/A</span>;
        }
        // Color based on interpretation
        const color = riskAdjQ >= 0.5 ? '#389e0d' :  // dark green - excellent
                      riskAdjQ >= 0.3 ? '#52c41a' :  // green - good
                      riskAdjQ >= 0.1 ? '#fa8c16' :  // orange - moderate
                      riskAdjQ >= 0 ? '#999' :        // gray - poor but positive
                      '#ff4d4f';                      // red - negative
        
        // DAS-2327: interpretation / expQ / resVol / returnProb locals removed
        // — the shared RewardRiskTooltip reads them directly from `record`.

        // DAS-2089: Week-ago comparison
        const riskAdjWeekAgo = record.risk_adj_q_return_week_ago;
        const riskAdjDelta = (riskAdjQ != null && riskAdjWeekAgo != null) ? riskAdjQ - riskAdjWeekAgo : null;
        const riskAdjArrow = riskAdjDelta != null && riskAdjDelta !== 0
          ? (riskAdjDelta > 0 ? <span style={{ color: '#52c41a', marginLeft: 2 }}>▲</span> : <span style={{ color: '#ff4d4f', marginLeft: 2 }}>▼</span>)
          : null;
        
        return (
          <Tooltip
            overlayStyle={{ maxWidth: 360 }}
            title={<RewardRiskTooltip record={record} />}
          >
            <span style={{ cursor: 'help' }}>
              <span style={{ color, fontWeight: 'bold' }}>{riskAdjQ.toFixed(2)}</span>
              {riskAdjArrow}
              {riskAdjWeekAgo != null && <span style={{ color: '#888', fontSize: '10px', marginLeft: 3 }}>({riskAdjWeekAgo.toFixed(2)})</span>}
            </span>
          </Tooltip>
        );
      },
    },
    {
      // DAS-1940/2122/2123: Rebalance Score column
      title: (
        <Tooltip title="Global composite ranking from Select Now: combines ROA and P/B value scores. #1 = strongest. Green (#1-10), orange (#11-30), grey (#31+). A green '✓ BUY' badge means the ticker also passes the sector-neutral filter and would be recommended by Select Now. 'not selected' means it ranks well globally but didn't make the top % within its sector. An orange '⚠ momentum' badge means the ticker was excluded by the momentum filter (negative 6-month return) but is shown for reference because it's in your portfolio. Italic grey scores are stale (> 1 week old).">
          <span>
            Rebalance<br/>Score <InfoCircleOutlined style={{ marginLeft: '4px' }} />
          </span>
        </Tooltip>
      ),
      key: 'rebalance_score',
      width: 115,
      sorter: (a, b) => {
        const scoreA = rebalanceScores?.[a.Ticker?.toUpperCase()]?.position ?? 9999;
        const scoreB = rebalanceScores?.[b.Ticker?.toUpperCase()]?.position ?? 9999;
        return scoreA - scoreB;
      },
      render: (_, record) => {
        const ticker = record.Ticker?.toUpperCase();
        const score = rebalanceScores?.[ticker];
        
        // Loading state
        if (rebalanceLoading) {
          return <LoadingOutlined style={{ color: '#1890ff' }} />;
        }
        
        // Error state - no config
        if (rebalanceError === 'no_config') {
          return (
            <Tooltip title="No saved Select Now configuration. Save a Select Now result first from Backtesting > Run History.">
              <span style={{ color: '#ff4d4f', cursor: 'help' }}>—</span>
            </Tooltip>
          );
        }
        
        // Has score - show position with color coding
        if (score) {
          const position = score.position;
          const totalUniverse = rebalanceInfo?.totalUniverse || rebalanceInfo?.totalRanked || '?';
          const isStale = score.stale === true;
          const isMomentumFiltered = score.momentumFiltered === true;
          
          // DAS-2156 Fix B: Stale scores — show last known position in muted style
          if (isStale) {
            return (
              <Tooltip title={`Last ranked #${position} | Score: ${score.score} | Last scored: ${score.staleDate}\n\nThis score is older than 1 week. Click 'Refresh Scores' for current ranking.`}>
                <div style={{ textAlign: 'center', opacity: 0.5 }}>
                  <div style={{ color: '#999', fontSize: '12px', fontStyle: 'italic' }}>
                    #{position}
                  </div>
                  <div style={{ fontSize: '9px', color: '#bbb' }}>
                    stale
                  </div>
                  <div style={{ fontSize: '8px', color: '#ccc' }}>
                    {score.staleDate}
                  </div>
                </div>
              </Tooltip>
            );
          }
          
          // DAS-2156 Fix A: Momentum-filtered portfolio tickers — show rank in italic with indicator
          if (isMomentumFiltered) {
            const posColor = position <= 10 ? '#52c41a' : position <= 30 ? '#fa8c16' : '#999';
            const posWeekAgo = record.select_now_position_week_ago;
            const posDelta = (position != null && posWeekAgo != null) ? position - posWeekAgo : null;
            const posArrow = posDelta != null && posDelta !== 0
              ? (posDelta < 0 ? <span style={{ color: '#52c41a', marginLeft: 2, fontSize: '10px' }}>▲</span> : <span style={{ color: '#ff4d4f', marginLeft: 2, fontSize: '10px' }}>▼</span>)
              : null;
            return (
              <Tooltip title={`Position #${position} of ${totalUniverse} | Score: ${score.score}${posWeekAgo != null ? ` | Week ago: #${posWeekAgo}` : ''}\n\nExcluded by momentum filter (negative 6-month return). Ranked for reference but would not appear in Select Now recommendations.`}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontWeight: 'bold', color: posColor, fontSize: '14px', fontStyle: 'italic' }}>
                    #{position}{posArrow}
                  </div>
                  {posWeekAgo != null && <div style={{ fontSize: '9px', color: '#888' }}>(#{posWeekAgo})</div>}
                  <div style={{ fontSize: '9px', marginTop: 1 }}>
                    <span style={{ 
                      color: '#fff',
                      backgroundColor: '#faad14',
                      padding: '0 4px', 
                      borderRadius: '3px',
                      fontSize: '8px',
                      fontWeight: 'bold'
                    }}>
                      ⚠ momentum
                    </span>
                  </div>
                </div>
              </Tooltip>
            );
          }
          
          // DAS-1946: Handle LOW_RANKED tickers (scored but outside top percentile)
          if (score.trade === 'LOW_RANKED') {
            const posWeekAgoLR = record.select_now_position_week_ago;
            const posDeltaLR = (position != null && posWeekAgoLR != null) ? position - posWeekAgoLR : null;
            const posArrowLR = posDeltaLR != null && posDeltaLR !== 0
              ? (posDeltaLR < 0 ? <span style={{ color: '#52c41a', marginLeft: 2, fontSize: '9px' }}>▲</span> : <span style={{ color: '#ff4d4f', marginLeft: 2, fontSize: '9px' }}>▼</span>)
              : null;
            const snDateLR = record.select_now_epoch ? new Date(record.select_now_epoch * 1000).toLocaleDateString() : (rebalanceInfo?.asOfDate || null);
            return (
              <Tooltip title={`Ranked #${position} of ${totalUniverse} in universe | Score: ${score.score}${posWeekAgoLR != null ? ` | Week ago: #${posWeekAgoLR}` : ''}${snDateLR ? ` | As of: ${snDateLR}` : ''}\n\nThis ticker is scored but ranked outside the selection threshold (top 10% + 50 runners-up)`}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ color: '#999', fontSize: '12px' }}>
                    #{position}{posArrowLR}
                  </div>
                  {posWeekAgoLR != null && <div style={{ fontSize: '9px', color: '#888' }}>(#{posWeekAgoLR})</div>}
                  <div style={{ fontSize: '9px', color: '#bbb' }}>
                    of {totalUniverse}
                  </div>
                </div>
              </Tooltip>
            );
          }
          
          // Normal ranked tickers (in top percentile + runners-up)
          const posColor = position <= 10 ? '#52c41a' : position <= 30 ? '#fa8c16' : '#666';
          const tradeColor = score.trade === 'BUY' ? '#52c41a' : score.trade === 'SELL' ? '#ff4d4f' : '#666';
          
          const isSelected = score.selected === true;
          const selectedTrade = score.selectedTrade;
          
          const posWeekAgo = record.select_now_position_week_ago;
          const posDelta = (position != null && posWeekAgo != null) ? position - posWeekAgo : null;
          const posArrow = posDelta != null && posDelta !== 0
            ? (posDelta < 0 ? <span style={{ color: '#52c41a', marginLeft: 2, fontSize: '10px' }}>▲</span> : <span style={{ color: '#ff4d4f', marginLeft: 2, fontSize: '10px' }}>▼</span>)
            : null;
          
          const snDate = record.select_now_epoch ? new Date(record.select_now_epoch * 1000).toLocaleDateString() : (rebalanceInfo?.asOfDate || null);
          const selectionLabel = isSelected 
            ? `✓ Selected (${selectedTrade})` 
            : (score.selected === false ? '✗ Not in sector selection' : '');
          return (
            <Tooltip title={`Position #${position} of ${totalUniverse} | Trade: ${score.trade} | Score: ${score.score}${selectionLabel ? ` | ${selectionLabel}` : ''}${posWeekAgo != null ? ` | Week ago: #${posWeekAgo}` : ''}${snDate ? ` | As of: ${snDate}` : ''}`}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontWeight: 'bold', color: posColor, fontSize: '14px' }}>
                  #{position}{posArrow}
                </div>
                {posWeekAgo != null && <div style={{ fontSize: '9px', color: '#888' }}>(#{posWeekAgo})</div>}
                <div style={{ fontSize: '10px', color: tradeColor }}>
                  {score.trade}
                </div>
                {isSelected && (
                  <div style={{ fontSize: '9px', marginTop: 1 }}>
                    <span style={{ 
                      color: selectedTrade === 'BUY' ? '#fff' : selectedTrade === 'SELL' ? '#fff' : '#666',
                      backgroundColor: selectedTrade === 'BUY' ? '#52c41a' : selectedTrade === 'SELL' ? '#ff4d4f' : '#d9d9d9',
                      padding: '0 4px', 
                      borderRadius: '3px',
                      fontWeight: 'bold'
                    }}>
                      ✓ {selectedTrade}
                    </span>
                  </div>
                )}
                {score.selected === false && (
                  <div style={{ fontSize: '9px', color: '#bbb', marginTop: 1 }}>
                    not selected
                  </div>
                )}
              </div>
            </Tooltip>
          );
        }
        
        // DAS-1943/1969: Display for tickers not in Select Now universe
        // Handle both null and undefined (precomputed scores might not include all tickers)
        if (rebalanceScores && !score) {
          // DAS-2156 Fix C: Informative tooltip explaining why no score exists
          if (rebalanceInfo?.precomputed) {
            const isNonUS = (ticker || '').includes('.');  // DAS-2692: guard — a position row can have no ticker (crashed on Saxo)
            const reason = isNonUS
              ? `No score for ${ticker}. Possible reasons: missing fundamental data (Market Cap / P/B unavailable for this exchange) or excluded by config filters.`
              : `No score for ${ticker}. Possible reasons: excluded by momentum filter (negative 6-month return), missing fundamental data, or excluded by config filters.`;
            return (
              <Tooltip title={`${reason} Click 'Refresh Scores' to recalculate.`}>
                <div style={{ textAlign: 'center' }}>
                  <span style={{ color: '#999' }}>—</span>
                  <div style={{ fontSize: '8px', color: '#ccc' }}>no data</div>
                </div>
              </Tooltip>
            );
          }
          
          // Build explanation based on ticker characteristics and config filters
          const filters = rebalanceInfo?.configFilters || {};
          const isNonUS = (ticker || '').includes('.');  // DAS-2692: guard — position row can have no ticker
          const stockRegion = filters.stock_region || 'all';
          const minMarketCap = filters.min_market_cap_eur;
          
          let reasons = [];
          
          // Check region mismatch first (most likely reason for .XX tickers)
          if (stockRegion === 'us_only' && isNonUS) {
            reasons.push('Config set to US stocks only');
          } else if (stockRegion === 'non_us_only' && !isNonUS) {
            reasons.push('Config set to non-US stocks only');
          } else {
            // No region mismatch - ticker might not be in the universe database
            // or could be missing market cap / fundamental data
            reasons.push('Ticker not in universe database');
            if (minMarketCap && minMarketCap > 0) {
              reasons.push(`Or: market cap below €${(minMarketCap / 1e9).toFixed(1)}B threshold`);
            }
            reasons.push('Or: missing fundamental data (ROA/P-B)');
          }
          
          const tooltipText = `Not in Select Now universe:\n• ${reasons.join('\n• ')}`;
          
          return (
            <Tooltip title={tooltipText}>
              <span style={{ color: '#999', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                <span style={{ fontSize: '12px' }}>—</span>
                <InfoCircleOutlined style={{ fontSize: '10px', opacity: 0.7 }} />
              </span>
            </Tooltip>
          );
        }
        
        // No scores loaded yet - show dash (user clicks header button to load)
        return <span style={{ color: '#999' }}>—</span>;
      },
    },
    {
      title: 'Zacks',
      key: 'zacks',
      width: 100,
      render: (_, record) => {
        const { zacks_ranking, zacks_growth, zacks_value, zacks_vgm } = record;
        if (!zacks_ranking && !zacks_growth && !zacks_value && !zacks_vgm) {
          return <span style={{ color: '#999' }}>N/A</span>;
        }
        
        // Zacks ranking color: 1-2 green, 3 yellow, 4-5 red
        const getRankColor = (rank) => {
          if (!rank) return '#999';
          if (rank <= 2) return '#52c41a';
          if (rank === 3) return '#faad14';
          return '#ff4d4f';
        };
        
        // Style letter grades: A/B green, C yellow, D/F red
        const getGradeColor = (grade) => {
          if (!grade) return '#999';
          const g = grade.toUpperCase();
          if (g === 'A' || g === 'B') return '#52c41a';
          if (g === 'C') return '#faad14';
          return '#ff4d4f';
        };
        
        return (
          <Tooltip title={`Rank: ${zacks_ranking || 'N/A'} | Growth: ${zacks_growth || 'N/A'} | Value: ${zacks_value || 'N/A'} | VGM: ${zacks_vgm || 'N/A'}`}>
            <div style={{ display: 'flex', gap: '3px', flexWrap: 'wrap' }}>
              {zacks_ranking && (
                <span style={{ 
                  backgroundColor: getRankColor(zacks_ranking), 
                  color: 'white', 
                  padding: '1px 5px', 
                  borderRadius: '3px',
                  fontSize: '11px',
                  fontWeight: 'bold'
                }}>
                  {zacks_ranking}
                </span>
              )}
              {zacks_growth && (
                <span style={{ 
                  color: getGradeColor(zacks_growth),
                  fontSize: '11px',
                  fontWeight: 'bold'
                }}>
                  G:{zacks_growth}
                </span>
              )}
              {zacks_value && (
                <span style={{ 
                  color: getGradeColor(zacks_value),
                  fontSize: '11px',
                  fontWeight: 'bold'
                }}>
                  V:{zacks_value}
                </span>
              )}
              {zacks_vgm && (
                <span style={{ 
                  color: getGradeColor(zacks_vgm),
                  fontSize: '11px',
                  fontWeight: 'bold'
                }}>
                  VGM:{zacks_vgm}
                </span>
              )}
            </div>
          </Tooltip>
        );
      },
    },
    {
      // DAS-2253: Sector column with trend icons
      title: 'Sector',
      key: 'sector_info',
      width: 150,
      render: (_, record) => {
        const sector = record.sector;
        if (!sector) return <span style={{ color: '#999' }}>N/A</span>;

        const sectorTrend = record.sector_trend || {};
        const instShort = record.sector_inst_trend || {};
        const instMid = record.sector_inst_mid_trend || {};

        // Color map for sector trend direction
        const getTrendColor = (dir) => {
          if (!dir) return '#999';
          const d = dir.toLowerCase();
          if (d.includes('strong bullish')) return '#52c41a';
          if (d.includes('moderate bullish') || d.includes('mild bullish')) return '#73d13d';
          if (d.includes('bullish')) return '#95de64';
          if (d.includes('bearish')) return '#ff4d4f';
          return '#faad14'; // neutral
        };

        // Color map for institutional trend
        const getInstColor = (trend) => {
          if (!trend) return '#d9d9d9';
          const t = trend.toLowerCase();
          if (t.includes('strong buy')) return '#52c41a';
          if (t.includes('weak buy')) return '#73d13d';
          if (t.includes('strong sell')) return '#ff4d4f';
          if (t.includes('weak sell')) return '#ff7875';
          return '#d9d9d9'; // neutral - gray
        };

        const regionLabels = { 'Global': 'GL', 'USA': 'US', 'Europe': 'EU', 'China': 'CN' };
        const regionOrder = ['Global', 'USA', 'Europe', 'China'];

        return (
          <div>
            <div style={{ fontSize: '11px', fontWeight: 'bold', marginBottom: '2px' }}>{sector}</div>
            {/* Sector trend — single value, not regional */}
            {sectorTrend.trend_direction && (
              <Tooltip title={`Sector Trend: ${sectorTrend.trend_direction}${sectorTrend.bullish_pct != null ? ` (Bull ${sectorTrend.bullish_pct}% / Bear ${sectorTrend.bearish_pct}%)` : ''}`}>
                <Tag color={getTrendColor(sectorTrend.trend_direction)} style={{ margin: '0 0 2px 0', fontSize: '9px', lineHeight: '14px', height: '16px' }}>
                  {sectorTrend.trend_direction}
                </Tag>
              </Tooltip>
            )}
            {/* Institutional Short — per region */}
            {regionOrder.some(r => instShort[r]) && (
              <div style={{ display: 'flex', gap: '2px', flexWrap: 'wrap', marginBottom: '1px' }}>
                <span style={{ fontSize: '8px', color: '#999', width: '10px' }}>S</span>
                {regionOrder.filter(r => instShort[r]).map(r => (
                  <Tooltip key={r} title={`${r} Inst. (Short): ${instShort[r]}`}>
                    <span style={{
                      color: getInstColor(instShort[r]) === '#d9d9d9' ? '#999' : 'white',
                      backgroundColor: getInstColor(instShort[r]),
                      fontSize: '8px', fontWeight: 'bold', cursor: 'default',
                      borderRadius: '2px', padding: '0 2px', lineHeight: '13px'
                    }}>
                      {regionLabels[r] || r}
                    </span>
                  </Tooltip>
                ))}
              </div>
            )}
            {/* Institutional Mid — per region */}
            {regionOrder.some(r => instMid[r]) && (
              <div style={{ display: 'flex', gap: '2px', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '8px', color: '#999', width: '10px' }}>M</span>
                {regionOrder.filter(r => instMid[r]).map(r => (
                  <Tooltip key={r} title={`${r} Inst. (Mid): ${instMid[r]}`}>
                    <span style={{
                      color: getInstColor(instMid[r]) === '#d9d9d9' ? '#999' : 'white',
                      backgroundColor: getInstColor(instMid[r]),
                      fontSize: '8px', fontWeight: 'bold', cursor: 'default',
                      borderRadius: '2px', padding: '0 2px', lineHeight: '13px'
                    }}>
                      {regionLabels[r] || r}
                    </span>
                  </Tooltip>
                ))}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: 'ESG',
      key: 'esg',
      width: 140,
      render: (_, record) => renderESGRating(record),
    },
  ];

  // ---------------------------------------------------------------------------
  // DAS-2306: W&O reduced-column layout.
  // The W&O Portfolio view does not carry trend/consensus/ESG data — the funds
  // are mutual funds without per-ticker fundamentals. Show only what the user
  // can actually act on: Position size %, Price, user-editable Year-start price,
  // and YTD vs that year-start price.
  //
  // Total exposure (denominator for Position%) is summed from the rendered
  // rows, NOT from toplineData.totalValue, so the percentages always add to
  // 100% even when the row sum disagrees with Topline_Other.value (the
  // reconciliation banner surfaces that disagreement separately).
  // ---------------------------------------------------------------------------
  const wenoTotalExposure = (portfolioData || []).reduce(
    (sum, r) => sum + (Number(r.Value_eur) || 0),
    0,
  );

  const wenoColumns = [
    {
      title: 'Ticker / Company',
      key: 'ticker_company',
      width: 240,
      fixed: 'left', // DAS-3067: keep ticker visible while scrolling sideways
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>{record.Ticker || record.ticker || '—'}</div>
          <div style={{ fontSize: '12px', color: '#666' }}>{record.Company}</div>
        </div>
      ),
    },
    {
      title: 'Exposure',
      key: 'exposure',
      width: 140,
      sorter: (a, b) => (a.Value_eur || 0) - (b.Value_eur || 0),
      defaultSortOrder: 'descend',
      render: (_, record) => (
        <div>
          €{record.Value_eur ? Number(record.Value_eur).toLocaleString('nl-NL', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) : '0'}
          {record.Position ? <span style={{ color: '#888' }}> / {record.Position}</span> : null}
        </div>
      ),
    },
    {
      title: 'Position %',
      key: 'position_pct',
      width: 100,
      sorter: (a, b) => (a.Value_eur || 0) - (b.Value_eur || 0),
      render: (_, record) => {
        if (!wenoTotalExposure) return <span style={{ color: '#999' }}>N/A</span>;
        const pct = (Number(record.Value_eur) || 0) / wenoTotalExposure * 100;
        return <span>{pct.toFixed(1)}%</span>;
      },
    },
    {
      title: 'Price',
      key: 'price',
      width: 100,
      render: (_, record) => {
        const p = record.Last_Price;
        if (p === null || p === undefined) return <span style={{ color: '#999' }}>N/A</span>;
        return <span>€{Number(p).toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}</span>;
      },
    },
    {
      title: (
        <span>
          Year start price{' '}
          <Tooltip title="User-editable Jan-1 reference price. Used to compute YTD performance for W&O funds (no per-day price history is stored).">
            <InfoCircleOutlined />
          </Tooltip>
        </span>
      ),
      key: 'year_start_price',
      width: 160,
      render: (_, record) => {
        const ticker = record.Ticker || record.ticker;
        const inFlight = yspEdits[ticker];
        const stored = record.year_start_price;
        const value = (inFlight !== undefined) ? inFlight : (stored ?? null);
        return (
          <InputNumber
            value={value}
            min={0}
            max={1_000_000_000}
            step={0.01}
            precision={4}
            decimalSeparator=","
            disabled={!ticker || !!yspSaving[ticker]}
            onChange={(v) => setYspEdits(prev => ({ ...prev, [ticker]: v }))}
            onBlur={() => {
              const editVal = yspEdits[ticker];
              if (editVal === undefined) return;  // nothing edited
              saveYearStartPrice(ticker, editVal);
            }}
            onPressEnter={() => {
              const editVal = yspEdits[ticker];
              if (editVal === undefined) return;
              saveYearStartPrice(ticker, editVal);
            }}
            style={{ width: 130 }}
            placeholder="—"
          />
        );
      },
    },
    {
      title: (
        <span>
          YTD %{' '}
          <Tooltip title="(Price − Year start price) / Year start price × 100. Requires both values; '—' shown otherwise.">
            <InfoCircleOutlined />
          </Tooltip>
        </span>
      ),
      key: 'ytd',
      width: 90,
      sorter: (a, b) => {
        const ytdA = (a.Last_Price && a.year_start_price) ? (a.Last_Price - a.year_start_price) / a.year_start_price : -Infinity;
        const ytdB = (b.Last_Price && b.year_start_price) ? (b.Last_Price - b.year_start_price) / b.year_start_price : -Infinity;
        return ytdA - ytdB;
      },
      render: (_, record) => {
        const price = Number(record.Last_Price);
        const ysp = Number(record.year_start_price);
        if (!price || !ysp || Number.isNaN(price) || Number.isNaN(ysp) || ysp === 0) {
          return <span style={{ color: '#999' }}>—</span>;
        }
        const ytd = (price - ysp) / ysp * 100;
        const color = ytd >= 0 ? '#52c41a' : '#ff4d4f';
        return (
          <span style={{ color, fontWeight: 'bold' }}>
            {ytd >= 0 ? '+' : ''}{ytd.toFixed(2)}%
          </span>
        );
      },
    },
  ];

  // DAS-2306: W&O account uses the reduced layout. `account` is sent as
  // 'weno' lowercase from ToplineTable.handleAccountHeaderClick; be defensive
  // about case to avoid a silent mis-route.
  const isWeno = (account || '').toLowerCase() === 'weno';
  const activeColumns = isWeno ? wenoColumns : groupAccountColumns(columns);  // DAS-3292: backward / forecast headings

  // DAS-2306: Reconciliation banner — surface a divergence between the
  // per-row value sum and the backend-scraped Topline_Other.value (shown in
  // the modal header as "Value"). A €1 tolerance absorbs FX/rounding noise.
  const wenoReconcileDelta = isWeno && toplineData && toplineData.totalValue != null
    ? Number(toplineData.totalValue) - wenoTotalExposure
    : 0;
  const wenoReconcileMismatch = isWeno && Math.abs(wenoReconcileDelta) > 1;

  // Format currency
  const formatCurrency = (value) => {
    if (value === null || value === undefined) return 'N/A';
    return `€${parseFloat(value).toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Format performance with color
  const getPerformanceColor = (value) => {
    if (value === null || value === undefined) return '#999';
    return parseFloat(value) >= 0 ? '#52c41a' : '#ff4d4f';
  };

  return (
    <>
      <Modal
        title={
          <div>
            <div style={{ fontSize: '16px', fontWeight: 'bold' }}>{accountDisplayName || account} Portfolio</div>
            {toplineData && (
              <div style={{ fontSize: '13px', fontWeight: 'normal', marginTop: '8px', display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
                <span>
                  <strong>Value:</strong> {formatCurrency(toplineData.totalValue)}
                </span>
                <span>
                  <strong>Cash:</strong> {formatCurrency(toplineData.cash)}
                </span>
                <span>
                  <strong>Return:</strong>{' '}
                  <span style={{ color: getPerformanceColor(toplineData.result) }}>
                    {formatCurrency(toplineData.result)}
                  </span>
                </span>
                <span>
                  <strong>Performance:</strong>{' '}
                  <span style={{ color: getPerformanceColor(toplineData.performance) }}>
                    {toplineData.performance !== null && toplineData.performance !== undefined 
                      ? `${toplineData.performance}%` 
                      : 'N/A'}
                  </span>
                </span>
              </div>
            )}
          </div>
        }
        open={visible}
        onCancel={onClose}
        // DAS-3067: no fixed pixel cap — grow with the window so a wide screen
        // shows the whole table; on narrower screens the table scrolls with
        // the Ticker column pinned.
        width="96vw"
        style={{ top: 20 }}
        footer={null}
      >
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Spin size="large" />
            <div style={{ marginTop: '16px' }}>Loading portfolio data...</div>
          </div>
        ) : (
          <>
            <div style={{ marginBottom: '16px', color: '#666', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
              <span>Showing {portfolioData.length} positions in {accountDisplayName || account}</span>
              
              {/* DAS-1940/1969/2121/2154: Rebalance info with run/config traceability */}
              {rebalanceInfo && (
                rebalanceInfo.precomputed ? (
                  <Tooltip title={`Cached scores from daily batch.${rebalanceInfo.asOfDate ? ` Data as of ${rebalanceInfo.asOfDate}.` : ''}${rebalanceInfo.runId ? ` Based on Run #${rebalanceInfo.runId}` : ''}${rebalanceInfo.configId ? ` (Config #${rebalanceInfo.configId})` : ''}. Auto-loads when < 1 week old. Click "Refresh Scores" to recalculate.`}>
                    <span style={{ fontSize: '12px', color: '#52c41a', cursor: 'help' }}>
                      <InfoCircleOutlined style={{ marginRight: '4px' }} />
                      Cached scores{rebalanceInfo.configName ? ` — "${rebalanceInfo.configName}"` : ''}{rebalanceInfo.runId ? ` (Run #${rebalanceInfo.runId})` : ''}{rebalanceInfo.asOfDate ? ` — ${rebalanceInfo.asOfDate}` : ''}
                    </span>
                  </Tooltip>
                ) : rebalanceInfo.source === 'precomputed' ? (
                  <Tooltip title={`Pre-computed daily scores.${rebalanceInfo.runId ? ` Based on Run #${rebalanceInfo.runId}` : ''}${rebalanceInfo.configId ? ` (Config #${rebalanceInfo.configId})` : ''}${rebalanceInfo.computedAt ? `. Computed at ${rebalanceInfo.computedAt}` : ''}${rebalanceInfo.asOfDate ? `. Data as of ${rebalanceInfo.asOfDate}` : ''}.`}>
                    <span style={{ fontSize: '12px', color: '#52c41a', cursor: 'help' }}>
                      <InfoCircleOutlined style={{ marginRight: '4px' }} />
                      Scores from: {rebalanceInfo.configName || 'Pre-computed'}{rebalanceInfo.runId ? ` (Run #${rebalanceInfo.runId})` : ''}{rebalanceInfo.computedAt ? ` — computed ${rebalanceInfo.computedAt}` : rebalanceInfo.asOfDate ? ` — ${rebalanceInfo.asOfDate}` : ''}
                    </span>
                  </Tooltip>
                ) : (
                  <span style={{ fontSize: '12px', color: '#1890ff' }}>
                    Rebalance scores from: {rebalanceInfo.configName || `Run #${rebalanceInfo.runId}`}{rebalanceInfo.configId ? ` (Config #${rebalanceInfo.configId})` : ''}{rebalanceInfo.asOfDate ? ` (as of ${rebalanceInfo.asOfDate})` : ''}
                  </span>
                )
              )}
              {rebalanceError === 'no_config' && (
                <span style={{ fontSize: '12px', color: '#ff4d4f' }}>
                  No saved Select Now config. Save one from Backtesting → Run History → Select Now.
                </span>
              )}
              {/* DAS-1969: Show button to calculate (if no scores) or refresh (if scores exist) */}
              {!rebalanceLoading && !rebalanceError && (
                <Button 
                  type={rebalanceScores ? "default" : "primary"}
                  size="small" 
                  icon={<CalculatorOutlined />}
                  onClick={fetchRebalanceScores}
                >
                  {rebalanceScores ? 'Refresh Scores' : 'Calculate Rebalance Scores'}
                </Button>
              )}
              {rebalanceLoading && (
                <span style={{ fontSize: '12px', color: '#1890ff' }}>
                  <LoadingOutlined style={{ marginRight: '8px' }} />
                  Calculating rebalance scores...
                </span>
              )}
            </div>
            {portfolioData.length === 0 && debugInfo && (
              <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#fff7e6', border: '1px solid #ffd591', borderRadius: '4px' }}>
                <strong>Debug Info:</strong>
                <div>Requested account: <code>{debugInfo.requested_account}</code></div>
                <div>Mapped to: <code>{debugInfo.mapped_to}</code></div>
                <div>Available accounts for this user: <code>{debugInfo.available_accounts?.join(', ') || 'none'}</code></div>
              </div>
            )}
            {/* DAS-2306: Reconciliation banner — only on W&O view, only when
                row-sum (sum of Value_eur) disagrees with the backend topline
                value (Topline_Other.value, shown in the header) by > €1. */}
            {wenoReconcileMismatch && (
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: '12px' }}
                message="W&O value mismatch between rows and topline"
                description={
                  <span>
                    Row sum: <strong>{formatCurrency(wenoTotalExposure)}</strong>
                    {' · '}
                    Topline (Topline_Other.value): <strong>{formatCurrency(toplineData.totalValue)}</strong>
                    {' · '}
                    Δ: <strong style={{ color: wenoReconcileDelta > 0 ? '#ff4d4f' : '#52c41a' }}>
                      {wenoReconcileDelta > 0 ? '+' : ''}{formatCurrency(wenoReconcileDelta)}
                    </strong>
                    {'. '}
                    A non-zero delta usually means the W&O scraper has not run
                    recently, or a fund position changed without a fresh scrape.
                  </span>
                }
              />
            )}
            <div data-testid="account-portfolio-table-das3067">
              <Table
                dataSource={portfolioData}
                columns={activeColumns}
                rowKey={(record) => record.Ticker || record.ticker || record.id}
                pagination={false}
                scroll={{ x: 'max-content' }}
                size="small"
              />
            </div>
            {account === 'saxo' && <SaxoSwitchScores />}
          </>
        )}
      </Modal>
      
      {isTickerModalVisible && (
        <ThreeTablesModal
          tickerValue={selectedTicker}
          open={isTickerModalVisible}
          onClose={() => setIsTickerModalVisible(false)}
          userId={userId}
        />
      )}
      
      {/* DAS-2157: Alert detail dialog */}
      <Dialog
        open={alertDialogOpen}
        onClose={() => { setAlertDialogOpen(false); setSelectedAlert(null); }}
        maxWidth={false}
        fullWidth={false}
        PaperProps={{
          style: { maxHeight: '90vh', overflow: 'auto', width: 'auto', maxWidth: '90vw' }
        }}
      >
        <DialogTitle>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>Alert Details — {selectedAlert?.ticker}</span>
            <Button type="text" onClick={() => { setAlertDialogOpen(false); setSelectedAlert(null); }} style={{ minWidth: 'auto' }}>✕</Button>
          </div>
        </DialogTitle>
        <DialogContent>
          {selectedAlert && (
            <AlertCard
              alert={selectedAlert}
              onClose={() => { setAlertDialogOpen(false); setSelectedAlert(null); }}
              onCheckboxChange={() => {}}
              onFollowedUpChange={() => {}}
              onReinvestmentCheck={() => {}}
              onTickerClick={(t) => { setSelectedTicker(t); setIsTickerModalVisible(true); }}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AccountPortfolioModal;
